import * as Toast from '@radix-ui/react-toast'
import { X, CheckCircle2, AlertCircle, Info, AlertTriangle } from 'lucide-react'
import { useToastStore } from '../store/toastStore'
import { clsx } from 'clsx'

export default function ToastService() {
  const { toasts, removeToast } = useToastStore()

  return (
    <Toast.Provider swipeDirection="right">
      {toasts.map(({ id, title, description, type }) => (
        <Toast.Root
          key={id}
          className={clsx(
            'group relative flex w-full max-w-[360px] flex-col overflow-hidden rounded-2xl border bg-white p-4 shadow-2xl transition-all',
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=move]:transition-none data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full md:data-[state=open]:slide-in-from-bottom-right-full',
            type === 'success' && 'border-green-100 bg-green-50/30',
            type === 'error' && 'border-red-100 bg-red-50/30',
            type === 'warning' && 'border-amber-100 bg-amber-50/30',
            type === 'info' && 'border-brand-100 bg-brand-50/30'
          )}
          onOpenChange={(open) => !open && removeToast(id)}
        >
          <div className="flex gap-3">
            <div className={clsx(
              'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border shadow-sm transition-colors',
              type === 'success' && 'border-green-200 bg-green-100 text-green-600',
              type === 'error' && 'border-red-200 bg-red-100 text-red-600',
              type === 'warning' && 'border-amber-200 bg-amber-100 text-amber-600',
              type === 'info' && 'border-brand-200 bg-brand-100 text-brand-600'
            )}>
              {type === 'success' && <CheckCircle2 size={20} />}
              {type === 'error' && <AlertCircle size={20} />}
              {type === 'warning' && <AlertTriangle size={20} />}
              {type === 'info' && <Info size={20} />}
            </div>

            <div className="flex-1 space-y-1 pt-0.5">
              <Toast.Title className="text-sm font-bold text-gray-900 tracking-tight">
                {title}
              </Toast.Title>
              {description && (
                <Toast.Description className="text-xs font-medium text-gray-500 leading-relaxed">
                  {description}
                </Toast.Description>
              )}
            </div>

            <Toast.Close className="h-8 w-8 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-900 hover:bg-white/50 transition-all focus:outline-none">
              <X size={16} strokeWidth={2.5} />
            </Toast.Close>
          </div>
          
          {/* Progress bar at bottom for visual duration indication */}
          <div className="absolute bottom-0 left-0 h-1 w-full bg-gray-100/50">
            <div className={clsx(
              'h-full animate-progress-shrink origin-left',
              type === 'success' && 'bg-green-500',
              type === 'error' && 'bg-red-500',
              type === 'warning' && 'bg-amber-500',
              type === 'info' && 'bg-brand-500'
            )} />
          </div>
        </Toast.Root>
      ))}
      <Toast.Viewport className="fixed bottom-0 right-0 z-[1000] m-0 flex flex-col gap-2 p-6 outline-none list-none overflow-hidden max-w-[100vw]" />
    </Toast.Provider>
  )
}
